import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import SimpleAnalysisTemplateSelector from 'analysis-tool/templates/SimpleAnalysisTemplateSelector';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {SCHEMAS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SavingState} from 'constants/RegistryConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  useContainerSchemasQueryWithRegistry,
  usePlateSchemasQueryWithRegistry,
  useLocationSchemasQueryWithRegistry,
  useBoxSchemasQueryWithRegistry,
} from 'inventory/useStorableSchemasQuery';
import {AutomationInputFileConfigListEditor} from 'lab-automation/settings/automation-input-file-config/AutomationInputFileConfigListEditor';
import {AutomationOutputFileConfigListEditor} from 'lab-automation/settings/automation-output-file-config/AutomationOutputFileConfigListEditor';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
  FileConfigErrors,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {getStore} from 'redux-stores/Store';
import FieldTable, {ClientSchemaField, FieldTableLoading} from 'registry/field/FieldTable';
import {useAllRequestSchemasQuery} from 'requests/schemas/useAllRequestSchemasQuery';
import {
  GetAnalysisTemplateForAssayRun,
  GetAnalysisTemplateForAssayRunVariables,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {
  filterFieldsForAnalysisTemplate,
  GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY,
  getExplicitConfigVariables,
  templateHasFileInputs,
} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import QueryingAnalysisTemplateVariableFieldTable, {
  AnalysisTemplateVariableFieldTable,
  SchemaLookupData,
} from 'results/analysis-integration/AnalysisTemplateVariableFieldTable';
import {useAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import {useAllRunSchemasQuery} from 'runs/run-schemas/useAllRunSchemasQuery';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getOwnerById} from 'schemas/SchemaUtil';
import {AssaySchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import {
  filterSchemasByOrganizationJson,
  filterSchemasByRegistryOrganization,
} from 'schemas/util/FilterSchemasByOrganization';
import {useAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';
import {useTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import runModal from 'ui-library/runModal';
import ErrorList from 'ui-library/spec-editor/ErrorList';
import * as modals from 'util/Modals';

import {AssayRunSchema, AssaySchema, getJsonUrl, SCHEMA_TYPE_NAME} from './AssaySchemaConstants';
import {AssaySchemaEditorBaseProps} from './AssaySchemaEditorTypes';
import {
  AssayRunSchemaSpecOverrides,
  getSpecForRequest,
  massageFieldsForSubmitWithSpecErrorHandling,
} from './AssaySchemaEditorUtils';
import AssaySchemaErrorList from './AssaySchemaErrorList';
import {ResultSchemaFieldTableRow, RunSchemaFieldTableRow} from './AssaySchemaFieldTableRow';
import ConfigureInputFileFiltersModal from './ConfigureInputFileFiltersModal';
import RunSchemaMetadataSection from './RunSchemaMetadataSection';
import SchemaMetadataSection, {schemaMetadataSectionStyles} from './SchemaMetadataSection';

export type AssaySchemaMetadataSettingsProps = AssaySchemaEditorBaseProps &
  (
    | {
        // Edit mode - schemaId exists
        schemaId: string;
        schema: AssaySchema;
        onUpdateAssaySchema: (params: {
          name?: string;
          sqlIdentifier?: string;
          spec?: AssaySchemaSpec | null;
          draft?: AssaySchemaSpec | null;
          overrides?: AssayRunSchemaSpecOverrides | null;
        }) => void;
        onUpdateAssaySchemaMetadata: (params: {
          name?: string;
          sqlIdentifier?: string;
          sessionId: string;
        }) => void;
        onCreateAssaySchema?: never;
        handleNext?: never;
      }
    | {
        // Create mode - schemaId is undefined
        schemaId?: undefined;
        schema: null;
        onUpdateAssaySchema?: never;
        onUpdateAssaySchemaMetadata?: never;
        onCreateAssaySchema?: (
          collaborators?: Array<import('auth/PermissionConstants').SchemaCollaborator>,
          overrides?: AssayRunSchemaSpecOverrides | null
        ) => void;
        handleNext?: () => void;
      }
  );

function getAnalysisTemplateSelectorState(
  schemaHasData: boolean,
  hasFileConfigs: boolean,
  connectionSchemaId: string | null
): {disabled: boolean; disabledTooltip: string | null} {
  if (schemaHasData) {
    return {
      disabled: true,
      disabledTooltip: 'Template cannot be changed when schema has data',
    };
  }
  if (hasFileConfigs) {
    return {
      disabled: true,
      disabledTooltip: 'Template cannot be selected when file configs are present',
    };
  }
  if (connectionSchemaId) {
    return {
      disabled: true,
      disabledTooltip:
        'Cannot be set while connection schema is set. Automation inputs are not limited by connection schemas.',
    };
  }
  return {
    disabled: false,
    disabledTooltip: null,
  };
}

function getConnectionSchemaSelectorState(analysisTemplateId: string | null): {
  disabled: boolean;
  disabledTooltip: string | null;
} {
  if (analysisTemplateId) {
    return {
      disabled: true,
      disabledTooltip:
        'Cannot be set while automation template is set. Automation inputs are not limited by connection schemas.',
    };
  }
  return {
    disabled: false,
    disabledTooltip: null,
  };
}

export default function AssaySchemaMetadataSettings(props: AssaySchemaMetadataSettingsProps): JSX.Element {
  const [specErrors, setSpecErrors] = useState<Array<string>>([]);
  const [fieldsErrors, setFieldsErrors] = useState<Array<string>>([]);
  const [inputFileConfigErrors, setInputFileConfigErrors] = useState<FileConfigErrors>({generic: []});
  const [outputFileConfigErrors, setOutputFileConfigErrors] = useState<FileConfigErrors>({generic: []});
  const [analysisTemplateError, setAnalysisTemplateError] = useState<string | null>(null);
  const [isLoadingAnalysisTemplate, setIsLoadingAnalysisTemplate] = useState(false);
  const [componentsAreValid, setComponentsAreValid] = useState({
    automationInputFileConfigs: true,
    automationOutputFileConfigs: true,
  });

  // Use effective state from props (managed by parent)
  // Note: effectiveAnalysisTemplateId can be null (user selected "None"), so we check for undefined
  const effectiveAnalysisTemplateId =
    props.effectiveAnalysisTemplateId !== undefined
      ? props.effectiveAnalysisTemplateId
      : props.analysisTemplateId;
  const draftTemplateFields = props.draftTemplateFields ?? null;
  const draftAnalysisTemplateVariableMapping = props.draftAnalysisTemplateVariableMapping ?? null;

  // Determine if showing persisted vs draft/preview
  const isShowingPersistedTemplate = effectiveAnalysisTemplateId === props.analysisTemplateId;

  const resultSchemasResult = useAllResultSchemasQuery();
  const requestSchemasResult = useAllRequestSchemasQuery();
  const runSchemasResult = useAllRunSchemasQuery();
  const boxSchemasResult = useBoxSchemasQueryWithRegistry();
  const containerSchemasResult = useContainerSchemasQueryWithRegistry();
  const locationSchemasResult = useLocationSchemasQueryWithRegistry();
  const plateSchemasResult = usePlateSchemasQueryWithRegistry();
  const tagSchemasResult = useTagSchemasWithRegistryQuery();
  const schemaInterfacesResult = useAllSchemaInterfacesQuery();

  const hasFileConfigs = props.inputFileConfigs.length > 0 || props.outputFileConfigs.length > 0;

  // Filter fields into run fields vs template fields based on effective template selection
  const {runFields, templateFields} = useMemo(() => {
    if (props.schemaType !== LegacyAssaySchemaType.ASSAY_RUN || !props.fields) {
      return {runFields: props.fields ?? [], templateFields: []};
    }

    return filterFieldsForAnalysisTemplate(
      props.fields,
      effectiveAnalysisTemplateId,
      props.analysisTemplateId,
      props.analysisTemplateVariableMapping,
      draftTemplateFields
    );
  }, [
    props.schemaType,
    props.fields,
    effectiveAnalysisTemplateId,
    props.analysisTemplateId,
    props.analysisTemplateVariableMapping,
    draftTemplateFields,
  ]);

  const linkableSchemas = useMemo(() => {
    if (
      resultSchemasResult.type === QueryResultType.SUCCESS &&
      requestSchemasResult.type === QueryResultType.SUCCESS &&
      runSchemasResult.type === QueryResultType.SUCCESS &&
      boxSchemasResult.type === QueryResultType.SUCCESS &&
      containerSchemasResult.type === QueryResultType.SUCCESS &&
      locationSchemasResult.type === QueryResultType.SUCCESS &&
      plateSchemasResult.type === QueryResultType.SUCCESS
    ) {
      const boxSchemas = filterSchemasByRegistryOrganization(boxSchemasResult.data.boxSchemas, props.ownerId);
      const containerSchemas = filterSchemasByRegistryOrganization(
        containerSchemasResult.data.containerSchemas,
        props.ownerId
      );
      const locationSchemas = filterSchemasByRegistryOrganization(
        locationSchemasResult.data.locationSchemas,
        props.ownerId
      );
      const plateSchemas = filterSchemasByRegistryOrganization(
        plateSchemasResult.data.plateSchemas,
        props.ownerId
      );

      const resultSchemas = filterSchemasByOrganizationJson(
        resultSchemasResult.data.resultSchemas,
        props.ownerId
      );
      const runSchemas = filterSchemasByOrganizationJson(runSchemasResult.data.runSchemas, props.ownerId);
      const requestSchemas = filterSchemasByOrganizationJson(
        requestSchemasResult.data.requestSchemas,
        props.ownerId
      );

      return {
        assaySchemas: [
          ...graphQLArrayToREST('RunSchema', runSchemas),
          ...graphQLArrayToREST('RequestSchema', requestSchemas),
          ...graphQLArrayToREST('ResultSchema', resultSchemas),
        ],
        storableSchemas: [
          ...graphQLArrayToREST('ContainerSchema', containerSchemas),
          ...graphQLArrayToREST('BoxSchema', boxSchemas),
          ...graphQLArrayToREST('LocationSchema', locationSchemas),
          ...graphQLArrayToREST('PlateSchema', plateSchemas),
        ],
      };
    }
    return {
      assaySchemas: [],
      storableSchemas: [],
    };
  }, [
    resultSchemasResult,
    requestSchemasResult,
    runSchemasResult,
    boxSchemasResult,
    containerSchemasResult,
    locationSchemasResult,
    plateSchemasResult,
    props.ownerId,
  ]);

  // Build schema lookup data for analysis template field generation
  const schemaLookupData: SchemaLookupData | null = useMemo(() => {
    if (
      tagSchemasResult.type === QueryResultType.SUCCESS &&
      plateSchemasResult.type === QueryResultType.SUCCESS &&
      containerSchemasResult.type === QueryResultType.SUCCESS &&
      schemaInterfacesResult.type === QueryResultType.SUCCESS
    ) {
      const tagSchemas = filterSchemasByRegistryOrganization(tagSchemasResult.data.tagSchemas, props.ownerId);
      const plateSchemas = filterSchemasByRegistryOrganization(
        plateSchemasResult.data.plateSchemas,
        props.ownerId
      );
      const containerSchemas = filterSchemasByRegistryOrganization(
        containerSchemasResult.data.containerSchemas,
        props.ownerId
      );
      const schemaInterfaces = schemaInterfacesResult.data.schemaInterfaces.map(({id, name}) => ({id, name}));
      const registryId =
        plateSchemas[0]?.registry.id ?? tagSchemas[0]?.registry.id ?? containerSchemas[0]?.registry?.id;
      return {
        tagSchemas: graphQLArrayToREST('TagSchema', tagSchemas),
        plateSchemas: graphQLArrayToREST('PlateSchema', plateSchemas),
        containerSchemas: graphQLArrayToREST('ContainerSchema', containerSchemas),
        schemaInterfaces,
        registryId,
      };
    }
    return null;
  }, [tagSchemasResult, plateSchemasResult, containerSchemasResult, schemaInterfacesResult, props.ownerId]);

  // For persisted templates, clear error since we're just displaying saved fields
  useEffect(() => {
    if (isShowingPersistedTemplate && effectiveAnalysisTemplateId) {
      setAnalysisTemplateError(null);
    }
  }, [isShowingPersistedTemplate, effectiveAnalysisTemplateId]);

  useEffect(() => {
    const indexPattern = / at index (\d+)$/;
    const newSpecErrors = [];
    const newFieldsErrors = [];
    const newInputFileConfigErrors: FileConfigErrors = {generic: []};
    const newOutputFileConfigErrors: FileConfigErrors = {generic: []};
    const getKeyOfError = (errorMessage: string): keyof FileConfigErrors => {
      const match = errorMessage.match(indexPattern);
      if (!match) {
        return 'generic';
      }
      return parseInt(match[1], 10);
    };

    const addToErrorObj = (
      errorObj: FileConfigErrors,
      errorMessage: string,
      key: keyof FileConfigErrors
    ): void => {
      if (!errorObj[key]) {
        errorObj[key] = [];
      }
      errorObj[key].push(errorMessage.replace(indexPattern, ''));
    };

    if (props.errors != null) {
      for (const error of props.errors) {
        if (error.match(/^"fields.*$/)) {
          newFieldsErrors.push(error);
        } else if (error.match(/^"automationInputFileConfigs.*$/g)) {
          const key = getKeyOfError(error);
          addToErrorObj(newInputFileConfigErrors, error, key);
        } else if (error.match(/^"automationOutputFileConfigs.*$/g)) {
          const key = getKeyOfError(error);
          addToErrorObj(newOutputFileConfigErrors, error, key);
        } else {
          newSpecErrors.push(error);
        }
      }
    }

    setSpecErrors(newSpecErrors);
    setFieldsErrors(newFieldsErrors);
    setInputFileConfigErrors(newInputFileConfigErrors);
    setOutputFileConfigErrors(newOutputFileConfigErrors);
  }, [props.errors, analysisTemplateError]);

  const getDraftOverrides = (): AssayRunSchemaSpecOverrides | null =>
    !isShowingPersistedTemplate
      ? {
          analysisTemplateId: effectiveAnalysisTemplateId,
          runFields,
          templateFields,
          analysisTemplateVariableMapping: draftAnalysisTemplateVariableMapping,
          // Clear the filters mapping when the template is removed or changed, as the old variable IDs
          // are no longer valid. Set to undefined only when the persisted template ID matches the
          // effective template ID (no change), so the filters mapping is updated only via the modal.
          analysisTemplateDefaultFiltersMapping:
            effectiveAnalysisTemplateId == null || effectiveAnalysisTemplateId !== props.analysisTemplateId
              ? null
              : undefined,
        }
      : null;

  let buttonText: string;
  let dataTestAttributeElement: string;
  let onButtonClick: () => void;

  if (props.schemaId) {
    // Edit mode
    buttonText = 'Update';
    dataTestAttributeElement = 'update';
    onButtonClick = () => {
      props.onUpdateAssaySchema({overrides: getDraftOverrides()});
    };
  } else if (BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS && props.handleNext) {
    // Create mode with permissions
    buttonText = 'Next';
    dataTestAttributeElement = 'next';
    onButtonClick = props.handleNext;
  } else {
    // Create mode without permissions
    buttonText = 'Create';
    dataTestAttributeElement = 'create';
    onButtonClick = () => {
      props.onCreateAssaySchema?.(undefined, getDraftOverrides());
    };
  }

  const shouldAllowSchemaUpdates = (): boolean => {
    if (!props.schemaId) {
      return true;
    }
    // If schema already exists, respect schema permissions.
    return (
      !BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS ||
      (props.schemaPermissions != null && props.schemaPermissions.definitionEditable === true)
    );
  };

  const onEditJSONClick = async (): Promise<void> => {
    if (props.hasUnsavedData) {
      const confirm = await Promise.resolve(
        modals.confirm('Leave', props.unsavedDataMessage, ButtonStyle.NEGATIVE, {
          promiseCompatible: true,
          enableRedesign: true,
        })
      );
      if (!confirm) {
        return;
      }
    }
    analytics.trackEvent(SCHEMAS_ANALYTICS_ACTIONS.CLICKED_EDIT_SCHEMA_AS_JSON, {
      schema_type:
        props.schemaType === LegacyAssaySchemaType.ASSAY_RUN
          ? ModelTypeConstants.RUN_SCHEMA
          : ModelTypeConstants.RESULT_SCHEMA,
      schema_id: props.schemaId,
      session_id: props.sessionId,
    });
    BenchlingUtil.navigate(null, getJsonUrl(props.schemaType, props.schemaId));
  };

  // Common logic for saving fields to backend (edit mode only)
  const saveFieldsToBackend = (allFields: Array<ClientSchemaField>): void => {
    if (!props.schemaId) {
      return;
    }

    // Block autosave when there's an analysis template error
    if (analysisTemplateError) {
      return;
    }

    const fieldsWithoutCid: Array<SchemaField> = allFields.map((field) => {
      const {cid, ...fieldWithoutCid} = field;
      return fieldWithoutCid;
    }) as Array<SchemaField>; // TODO(30720) Fix SchemaField/ClientSchemaField type issues

    if (isEqual(fieldsWithoutCid, props.fields)) {
      return;
    }

    const draftSpec = getSpecForRequest(props);
    if (draftSpec == null) {
      return;
    }

    const massagedFields = massageFieldsForSubmitWithSpecErrorHandling(allFields);
    if (massagedFields == null) {
      return;
    }

    const spec = {...draftSpec, fields: massagedFields};
    props.onUpdateAssaySchema({spec, draft: draftSpec});
  };

  // Handler for run fields table - merges with template fields
  const handleRunFieldsChange = (fields: Array<ClientSchemaField>): void => {
    if (!props.schemaId) {
      getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecFields(fields));
      return;
    }

    // Edit mode - merge run fields with template fields if template exists
    if (effectiveAnalysisTemplateId && templateFields.length > 0) {
      saveFieldsToBackend([...fields, ...templateFields] as Array<ClientSchemaField>);
    } else {
      saveFieldsToBackend(fields);
    }
  };

  // Handler for template fields table - merges with run fields
  const handleTemplateFieldsChange = (fields: Array<ClientSchemaField>): void => {
    if (!props.schemaId) {
      return;
    }

    // Template fields are passed in, merge with run fields
    saveFieldsToBackend([...runFields, ...fields] as Array<ClientSchemaField>);
  };

  const getDisableSubmitWithTooltip = (): string | null => {
    if (!props.name) {
      return 'Schema needs a name';
    }
    if (props.fields?.some((f) => !f.name)) {
      return 'Schema fields must all have names';
    }
    if (!Object.values(componentsAreValid).every((isValid) => isValid)) {
      return 'Spec is invalid';
    }
    if (isLoadingAnalysisTemplate) {
      return 'Loading automation template fields...';
    }
    return null;
  };

  const disableSubmitWithTooltip = getDisableSubmitWithTooltip();

  const dataTestAttrs = {
    switchJson: {...props.baseTestAttrs, element: 'switchJson'},
  };

  // If the selected template is persisted to the run schema, then query for the analysis template's config
  // variables in order to allow the user to configure default filters for any FILE type config variables.
  const getAnalysisTemplateReponse = useSkippableBenchlingQuery<
    GetAnalysisTemplateForAssayRun,
    GetAnalysisTemplateForAssayRunVariables
  >(GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY, {
    variables: {analysisTemplateId: effectiveAnalysisTemplateId!},
    skip: !isShowingPersistedTemplate || effectiveAnalysisTemplateId == null,
  });

  return (
    <div className="u-flex-grow u-min-width-zero">
      <div className="settingsPageContainer-section">
        <SchemaMetadataSection
          isEditableWarehouseTableName={
            BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && shouldAllowSchemaUpdates()
          }
          name={props.name}
          ownerId={props.ownerId}
          ownerChoices={props.ownerChoices}
          sqlIdentifier={props.sqlIdentifier}
          onNameChange={(name: string) => {
            getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecName(name));
            if (props.schemaId) {
              props.onUpdateAssaySchemaMetadata({
                name,
                sessionId: props.sessionId,
              });
            }
          }}
          onOwnerChange={(owner) => {
            assert(!props.schemaId, 'Owner should only be editable when creating a new schema');
            getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecOwner(owner.id));
          }}
          onSqlIdentifierChange={(sqlIdentifier: string) => {
            getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecSqlIdentifier(sqlIdentifier));
            if (props.schemaId) {
              props.onUpdateAssaySchemaMetadata({
                sqlIdentifier,
                sessionId: props.sessionId,
              });
            }
          }}
          schema={props.schema}
        />
        {props.schemaType === LegacyAssaySchemaType.ASSAY_RUN &&
          (() => {
            const connectionSchemaState = getConnectionSchemaSelectorState(effectiveAnalysisTemplateId);
            return (
              <RunSchemaMetadataSection
                connectionSchemaId={props.connectionSchemaId}
                allowInsertRunFromInbox={props.allowInsertRunFromInbox}
                allowCreateRunFromNotebook={props.allowCreateRunFromNotebook}
                shouldRecordEquipment={props.shouldRecordEquipment}
                isCreating={props.schema == null}
                onUpdate={noop}
                connectionSchemaDisabled={connectionSchemaState.disabled}
                connectionSchemaDisabledTooltip={connectionSchemaState.disabledTooltip}
              />
            );
          })()}
        {props.schemaType === LegacyAssaySchemaType.ASSAY_RUN &&
          BenchlingConfig.ENABLE_DIRECT_RUN_TO_ANALYSIS_INTEGRATION && (
            <div className={css(schemaMetadataSectionStyles.schemaMetadataSection)}>
              <div className={css(schemaMetadataSectionStyles.schemaMetadataColumn)}>
                <div className={css(schemaMetadataSectionStyles.schemaMetadataField)}>
                  <FieldLabel label="Automation template" />
                  <SimpleAnalysisTemplateSelector
                    template={effectiveAnalysisTemplateId ? {id: effectiveAnalysisTemplateId} : null}
                    {...getAnalysisTemplateSelectorState(
                      props.schemaHasData,
                      hasFileConfigs,
                      props.connectionSchemaId
                    )}
                    onSelectTemplate={
                      shouldAllowSchemaUpdates() &&
                      !props.schemaHasData &&
                      !hasFileConfigs &&
                      !props.connectionSchemaId
                        ? (template) => {
                            props.onEffectiveAnalysisTemplateIdChange?.(template?.id ?? null);
                          }
                        : undefined
                    }
                    filterTemplate={(template) =>
                      template.dataFrameVariables.length === 0 &&
                      getExplicitConfigVariables(template.configVariables).length >= 1
                    }
                    includeNone={true}
                  />
                  {
                    // Only show "configure default input filters" if there is a non-null reponse (i.e. the
                    // effective template is the persisted template) and if that analysis template has FILE-type
                    // config variables.
                    getAnalysisTemplateReponse &&
                      getAnalysisTemplateReponse.type === QueryResultType.SUCCESS &&
                      !isInaccessible(getAnalysisTemplateReponse.data.analysisTemplate) &&
                      templateHasFileInputs(getAnalysisTemplateReponse.data.analysisTemplate) && (
                        <div
                          className={css(assaySchemaEditorStyles.configureFileFilters)}
                          onClick={() => {
                            const analysisTemplateForFiltersConfiguration =
                              getAnalysisTemplateReponse.data.analysisTemplate;
                            // The div only renders if the template is accessible, but we have to reassert for
                            // check typescript to pass
                            assert(
                              !isInaccessible(analysisTemplateForFiltersConfiguration),
                              'User was able to configure input filters on inaccessible template'
                            );
                            runModal(ConfigureInputFileFiltersModal, {
                              analysisTemplate: analysisTemplateForFiltersConfiguration,
                              initialDefaultFiltersMapping: props.analysisTemplateDefaultFiltersMapping,
                              onSaveDefaultFiltersMapping: (newDefaultFiltersMapping) => {
                                assert(
                                  props.onUpdateAssaySchema != null,
                                  'User was able to configure input filters before run schema was created.'
                                );
                                props.onUpdateAssaySchema({
                                  overrides: {
                                    analysisTemplateDefaultFiltersMapping: newDefaultFiltersMapping,
                                  },
                                });
                              },
                            });
                          }}
                        >
                          Configure default data source filters
                        </div>
                      )
                  }
                </div>
              </div>
            </div>
          )}
      </div>
      {analysisTemplateError && (
        <div>
          <ErrorList errorMessage={analysisTemplateError} />
          <br />
        </div>
      )}
      {(specErrors.length > 0 || props.errorMessage) && (
        <div>
          <ErrorList errors={specErrors} errorMessage={props.errorMessage} />
          <br />
        </div>
      )}
      {/* Preview mode: Show QueryingAnalysisTemplateVariableFieldTable to fetch and display draft template */}
      {props.schemaType === LegacyAssaySchemaType.ASSAY_RUN &&
        effectiveAnalysisTemplateId &&
        !isShowingPersistedTemplate && (
          <QueryingAnalysisTemplateVariableFieldTable
            analysisTemplateId={effectiveAnalysisTemplateId}
            ownerId={props.ownerId}
            ownerUrlId={getOwnerById(props.ownerId)?.url_identifier ?? ''}
            registryId={schemaLookupData?.registryId}
            onDraftFieldsChange={props.onDraftTemplateFieldsChange ?? noop}
            onAnalysisTemplateVariableMappingChange={
              props.onDraftAnalysisTemplateVariableMappingChange ?? noop
            }
            onError={setAnalysisTemplateError}
            setIsLoading={setIsLoadingAnalysisTemplate}
            key={effectiveAnalysisTemplateId}
            schemaLookupData={schemaLookupData}
          />
        )}
      {/* Persisted mode: Show AnalysisTemplateVariableFieldTable to display saved template fields */}
      {props.schemaType === LegacyAssaySchemaType.ASSAY_RUN &&
        isShowingPersistedTemplate &&
        templateFields.length > 0 && (
          <div>
            <AnalysisTemplateVariableFieldTable
              templateFields={templateFields}
              ownerId={props.ownerId}
              ownerUrlId={getOwnerById(props.ownerId)?.url_identifier ?? ''}
              registryId={schemaLookupData?.registryId}
              onFieldsChange={props.schema != null ? handleTemplateFieldsChange : undefined}
              isPreview={props.schema == null}
            />
          </div>
        )}
      <div className={css(assaySchemaEditorStyles.fieldSection)}>
        <AssaySchemaErrorList errors={fieldsErrors} component="fields configuration" />
        {resultSchemasResult.type === QueryResultType.SUCCESS &&
        requestSchemasResult.type === QueryResultType.SUCCESS &&
        runSchemasResult.type === QueryResultType.SUCCESS &&
        boxSchemasResult.type === QueryResultType.SUCCESS &&
        containerSchemasResult.type === QueryResultType.SUCCESS &&
        locationSchemasResult.type === QueryResultType.SUCCESS &&
        plateSchemasResult.type === QueryResultType.SUCCESS ? (
          <>
            {/* Show message when draft template is being previewed in edit mode */}
            {!isShowingPersistedTemplate && props.schema != null && (
              <p className={css(assaySchemaEditorStyles.templateFieldsDescription)}>
                Please save the selected automation template to edit the run fields.
              </p>
            )}
            {/* Run schema fields table - shows filtered run fields (excluding template fields) */}
            <FieldTable
              key={effectiveAnalysisTemplateId ?? 'no-template'}
              schemaId={props.schema ? props.schema.id : null}
              schema={props.schema}
              schemaHasData={props.schemaHasData}
              schemaFieldDependentsSummariesById={props.schemaFieldDependentsSummariesById}
              isBeingCreated={props.schema == null}
              isDisabled={
                !shouldAllowSchemaUpdates() || (!isShowingPersistedTemplate && props.schema != null)
              }
              itemClass={
                props.schemaType === LegacyAssaySchemaType.ASSAY_RESULT
                  ? ResultSchemaFieldTableRow
                  : RunSchemaFieldTableRow
              }
              fields={props.schemaType === LegacyAssaySchemaType.ASSAY_RUN ? runFields : (props.fields ?? [])}
              onFieldsChange={handleRunFieldsChange}
              schemaType={SCHEMA_TYPE_NAME[props.schemaType]}
              batchSchemas={[]}
              storableSchemas={linkableSchemas.storableSchemas}
              assaySchemas={linkableSchemas.assaySchemas}
              savingState={SavingState.COMPLETED}
              ownerUrlId={getOwnerById(props.ownerId)?.url_identifier ?? ''}
              ownerId={props.ownerId}
              shouldShowIsRequired={true}
              shouldShowIsIntegration={false}
              shouldShowIsParentLink={false}
              dataTestAttrs={{component: 'AssaySchemaMetadataSettings', element: 'fieldTable'}}
              enforceUniqueDisplayName={false}
            />
          </>
        ) : (
          <FieldTableLoading schemaType={SCHEMA_TYPE_NAME[props.schemaType]} />
        )}
      </div>
      {props.schemaType === LegacyAssaySchemaType.ASSAY_RUN &&
        props.schema != null &&
        !effectiveAnalysisTemplateId && (
          <>
            <div>
              <AutomationInputFileConfigListEditor
                baseSchema={props.schema as AssayRunSchema}
                errors={inputFileConfigErrors}
                automationInputFileConfigs={props.inputFileConfigs}
                onChange={(newValue: Array<AutomationInputFileConfigSpec>) => {
                  getStore().dispatch(SchemaConfigReduxActions.setSchemaInputFileConfig(newValue));
                }}
                onValidationChange={(isValid) => {
                  setComponentsAreValid({...componentsAreValid, automationInputFileConfigs: isValid});
                }}
              />
            </div>
            <div>
              <AutomationOutputFileConfigListEditor
                baseSchema={props.schema as AssayRunSchema}
                connectionSchemaId={props.connectionSchemaId}
                errors={outputFileConfigErrors}
                automationOutputFileConfigs={props.outputFileConfigs}
                onChange={(newValue: Array<ClientAutomationOutputFileConfigSpec>) => {
                  getStore().dispatch(SchemaConfigReduxActions.setSchemaOutputFileConfig(newValue));
                }}
                onValidationChange={(isValid) => {
                  setComponentsAreValid({...componentsAreValid, automationOutputFileConfigs: isValid});
                }}
                ownerId={props.ownerId}
              />
            </div>
          </>
        )}
      {(!props.schemaId || !props.autosave) && shouldAllowSchemaUpdates() && !analysisTemplateError && (
        <TooltipWrapper tooltipTitle={disableSubmitWithTooltip}>
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={disableSubmitWithTooltip != null}
            onClick={() => {
              onButtonClick();
            }}
            size={ButtonSize.MEDIUM}
            text={buttonText}
            dataTestAttrs={{...props.baseTestAttrs, element: dataTestAttributeElement}}
          />
        </TooltipWrapper>
      )}
      {shouldAllowSchemaUpdates() && !effectiveAnalysisTemplateId && isShowingPersistedTemplate && (
        <Button
          className="u-small-spacing-right"
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MEDIUM}
          id="editAsJSONButton"
          text="Edit as JSON"
          onClick={() => {
            onEditJSONClick();
          }}
          dataTestAttrs={dataTestAttrs.switchJson}
        />
      )}
    </div>
  );
}

export const assaySchemaEditorStyles = StyleSheet.create({
  fieldSection: {
    marginBottom: '32px',
  },
  templateFieldsDescription: {
    marginBottom: '8px',
    color: Color.GRAY_650,
    fontStyle: 'italic',
  },
  configureFileFilters: {
    display: 'inline-block',
    fontSize: '12px',
    color: Color.BLUE_500,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});
